import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Mic, Square, X, Send, Loader2, CheckCircle2, Bot, User as UserIcon } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { sendAuxiliarMessage, executeAuxiliarAction, transcribeAudio } from '../../services/api';

/**
 * Conversational AI assistant for creating documents directly from list pages.
 *
 * Flow:
 *   1. User types/speaks → backend Claude returns a structured action.
 *   2. If action is the target document → preview + "Confirmar y crear" button.
 *   3. If action is `create_client` → execute it, then ask user to confirm the document creation.
 *   4. If action is `clarify`/`info`/`need_more_info` → render the message and let the user reply.
 *
 *   <AiCreateButton entity="invoice" />
 */
const ENTITY_META = {
  invoice: {
    action: 'create_invoice',
    promptPrefix: 'Crea una FACTURA con estos datos:',
    label: 'factura',
    redirect: (res) => `/invoices/${res?.id || res?.data?.id || ''}/edit`,
    examples: [
      'Factura a Acme por 3 horas de consultoría a 75€',
      'Facturar 2 horas de diseño + 1 hora de soporte a Cliente XYZ',
    ],
  },
  quote: {
    action: 'create_quote',
    promptPrefix: 'Crea un PRESUPUESTO con estos datos:',
    label: 'presupuesto',
    redirect: (res) => `/quotes/${res?.id || res?.data?.id || ''}/edit`,
    examples: [
      'Presupuesto para Cliente Z: desarrollo web 1500€ + mantenimiento 200€/mes',
      'Presupuesto a Acme de 20 horas a 80€/hora',
    ],
  },
  proforma: {
    action: 'create_proforma',
    promptPrefix: 'Crea una PROFORMA con estos datos:',
    label: 'proforma',
    redirect: (res) => `/proformas/${res?.id || res?.data?.id || ''}/edit`,
    examples: [
      'Proforma a Cliente A: 5 licencias software 300€ cada una',
      'Proforma de 1000€ a Tienda Y por instalación',
    ],
  },
  delivery_note: {
    action: 'create_delivery_note',
    promptPrefix: 'Crea un ALBARÁN con estos datos:',
    label: 'albarán',
    redirect: (res) => `/delivery-notes/${res?.id || res?.data?.id || ''}/edit`,
    examples: [
      'Albarán entrega de 50 cajas de papel A4 a Oficinas Acme',
      'Albarán de 3 monitores y 1 portátil a Cliente Tech',
    ],
  },
  expense: {
    action: 'create_expense',
    promptPrefix: 'Registra un GASTO con estos datos:',
    label: 'gasto',
    redirect: () => '/expenses',
    examples: [
      'Gasto de 45€ en gasolina hoy',
      'Compra de material de oficina por 120€ con IVA del 21%',
    ],
  },
};

const fmtEur = (n) => new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(Number(n) || 0);

export const AiCreateButton = ({ entity = 'invoice', className = '', onCreated, testid }) => {
  const meta = ENTITY_META[entity] || ENTITY_META.invoice;
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]); // [{role:'user'|'ai', text, action?}]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [pendingAction, setPendingAction] = useState(null); // action awaiting "Confirmar y crear"
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const textareaRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      // Reset state when opening
      setMessages([]);
      setInput('');
      setPendingAction(null);
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pendingAction]);

  const close = () => {
    if (recording) stopRecording();
    setOpen(false);
  };

  // ----------------- Audio -----------------
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setBusy(true);
        try {
          const { data } = await transcribeAudio(blob);
          const transcript = data?.text || '';
          if (transcript) {
            setInput((prev) => prev ? `${prev}\n${transcript}` : transcript);
            toast.success('Audio transcrito');
          } else {
            toast.error('No se entendió el audio');
          }
        } catch {
          toast.error('Error transcribiendo audio');
        } finally {
          setBusy(false);
        }
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error('No se pudo acceder al micrófono');
    }
  };
  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  // ----------------- Conversation -----------------
  const sendMessage = async (rawText, opts = {}) => {
    const userText = (rawText ?? input).trim();
    if (!userText) return;
    setInput('');
    setPendingAction(null);

    // Append user message
    const isFirst = messages.length === 0;
    const wireMessage = isFirst ? `${meta.promptPrefix} ${userText}` : userText;
    const nextMessages = [...messages, { role: 'user', text: userText }];
    setMessages(nextMessages);
    setBusy(true);

    try {
      // Backend keeps conversation memory per user via LlmChat session_id,
      // so we don't need to ship the full transcript again.
      const { data } = await sendAuxiliarMessage(wireMessage);
      const ai = data?.response || {};
      const action = ai?.action;
      const aiMessage = ai?.message || ai?.response || '';

      // Show the AI's natural-language reply
      setMessages((prev) => [...prev, { role: 'ai', text: aiMessage, action: ai }]);

      if (action === meta.action) {
        // Target document → wait for user confirmation
        setPendingAction(ai);
      } else if (action === 'create_client') {
        // Auto-execute client creation, then ask user to continue
        try {
          const { data: newClient } = await executeAuxiliarAction(ai);
          setMessages((prev) => [
            ...prev,
            { role: 'ai', text: `✓ Cliente "${newClient?.name || ai.name}" creado. Cuéntame los conceptos y te genero el/la ${meta.label}.` },
          ]);
        } catch (e) {
          const det = e?.response?.data?.detail;
          setMessages((prev) => [
            ...prev,
            { role: 'ai', text: `No pude crear el cliente: ${typeof det === 'string' ? det : 'error desconocido'}. Inténtalo de nuevo.` },
          ]);
        }
      } else if (action === 'create_service') {
        try {
          const { data: newSvc } = await executeAuxiliarAction(ai);
          setMessages((prev) => [
            ...prev,
            { role: 'ai', text: `✓ Servicio "${newSvc?.name || ai.name}" creado. ¿Quieres usarlo ahora para el/la ${meta.label}?` },
          ]);
        } catch (e) {
          const det = e?.response?.data?.detail;
          setMessages((prev) => [
            ...prev,
            { role: 'ai', text: `No pude crear el servicio: ${typeof det === 'string' ? det : 'error desconocido'}.` },
          ]);
        }
      }
      // For 'clarify', 'info', 'need_more_info' → already shown via aiMessage; user keeps typing.
    } catch (e) {
      setMessages((prev) => [...prev, { role: 'ai', text: 'Hubo un error contactando con la IA. Inténtalo de nuevo.' }]);
    } finally {
      setBusy(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  };

  const confirmAndCreate = async () => {
    if (!pendingAction) return;
    setBusy(true);
    try {
      const { data: created } = await executeAuxiliarAction(pendingAction);
      toast.success(`${meta.label.charAt(0).toUpperCase() + meta.label.slice(1)} creado correctamente`);
      setOpen(false);
      if (onCreated) onCreated(created);
      else {
        const url = meta.redirect(created);
        if (url) navigate(url);
      }
    } catch (e) {
      const det = e?.response?.data?.detail;
      const msg = typeof det === 'string' ? det : `Error al crear ${meta.label}`;
      setMessages((prev) => [...prev, { role: 'ai', text: `❌ ${msg}. ¿Quieres ajustar algo?` }]);
      setPendingAction(null);
    } finally {
      setBusy(false);
    }
  };

  // ----------------- Render helpers -----------------
  const renderPendingPreview = () => {
    if (!pendingAction) return null;
    const items = pendingAction.items || [];
    const subtotal = items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);
    return (
      <div className="bg-violet-50/60 border-2 border-violet-200 rounded-2xl p-4 space-y-2" data-testid="ai-pending-preview">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-violet-600" />
          <p className="text-xs font-bold uppercase tracking-widest text-violet-700">Borrador propuesto</p>
        </div>
        {pendingAction.client_name && (
          <p className="text-sm font-bold text-stone-900">{pendingAction.client_name}</p>
        )}
        {items.length > 0 && (
          <ul className="text-xs space-y-1">
            {items.map((it, i) => (
              <li key={i} className="flex justify-between gap-3 text-stone-700">
                <span className="truncate">· {it.quantity}× {it.description}</span>
                <span className="font-mono whitespace-nowrap">{fmtEur((it.quantity || 0) * (it.unit_price || 0))}</span>
              </li>
            ))}
          </ul>
        )}
        {pendingAction.total !== undefined && (
          <p className="text-sm font-bold text-stone-900">Total: {fmtEur(pendingAction.total)}</p>
        )}
        {pendingAction.amount !== undefined && (
          <p className="text-sm font-bold text-stone-900">Importe: {fmtEur(pendingAction.amount)}</p>
        )}
        {items.length > 0 && pendingAction.total === undefined && (
          <p className="text-xs text-stone-500">Subtotal estimado: {fmtEur(subtotal)} (+ IVA según tipos indicados)</p>
        )}
      </div>
    );
  };

  return (
    <>
      <Button
        type="button"
        onClick={() => setOpen(true)}
        className={`group bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 hover:from-violet-600 hover:via-fuchsia-600 hover:to-pink-600 text-white rounded-full shadow-lg shadow-fuchsia-200/40 hover:-translate-y-0.5 transition-all duration-300 ${className}`}
        data-testid={testid || `ai-create-${entity}-button`}
      >
        <Sparkles className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform" />
        Crear con IA
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-2 sm:p-4 bg-black/40 backdrop-blur-sm" onMouseDown={close} data-testid="ai-create-modal">
          <div
            className="bg-white rounded-3xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-5 pb-3 flex items-center gap-3 border-b border-stone-100">
              <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500 flex items-center justify-center shadow-lg shadow-fuchsia-200/40">
                <Sparkles className="w-5 h-5 text-white" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-extrabold font-heading tracking-tight">Crear {meta.label} con IA</h3>
                <p className="text-[11px] sm:text-xs text-stone-500">Conversa con la IA · Crea clientes que no existan al vuelo</p>
              </div>
              <button onClick={close} className="p-1.5 hover:bg-stone-100 rounded-lg" data-testid="ai-modal-close">
                <X className="w-4 h-4 text-stone-400" />
              </button>
            </div>

            {/* Conversation feed */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 sm:px-5 py-3 space-y-3 min-h-[180px]">
              {messages.length === 0 ? (
                <div className="bg-stone-50/60 border border-stone-100 rounded-xl p-3">
                  <p className="text-[10px] uppercase font-bold tracking-widest text-stone-500 mb-1.5">Ejemplos · cliccar para auto-rellenar</p>
                  <ul className="space-y-1">
                    {meta.examples.map((ex, i) => (
                      <li key={i}>
                        <button
                          type="button"
                          onClick={() => setInput(ex)}
                          className="text-xs text-stone-600 hover:text-violet-700 text-left"
                        >
                          · {ex}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                messages.map((m, i) => (
                  <div key={i} className={`flex gap-2 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    {m.role === 'ai' && (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-3.5 h-3.5 text-white" />
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-3 py-2 max-w-[80%] text-sm whitespace-pre-line ${
                        m.role === 'user'
                          ? 'bg-violet-100 text-stone-900 rounded-tr-sm'
                          : 'bg-stone-100 text-stone-800 rounded-tl-sm'
                      }`}
                      data-testid={`ai-msg-${m.role}-${i}`}
                    >
                      {m.text}
                    </div>
                    {m.role === 'user' && (
                      <div className="w-7 h-7 rounded-full bg-stone-200 flex items-center justify-center flex-shrink-0">
                        <UserIcon className="w-3.5 h-3.5 text-stone-600" />
                      </div>
                    )}
                  </div>
                ))
              )}

              {busy && (
                <div className="flex items-center gap-2 text-xs text-stone-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Pensando…
                </div>
              )}

              {renderPendingPreview()}
            </div>

            {/* Input + actions */}
            <div className="border-t border-stone-100 p-3 sm:p-4 space-y-2">
              {pendingAction ? (
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => { setPendingAction(null); textareaRef.current?.focus(); }}
                    className="rounded-full flex-1"
                    data-testid="ai-ajustar"
                  >
                    Ajustar
                  </Button>
                  <Button
                    type="button"
                    onClick={confirmAndCreate}
                    disabled={busy}
                    className="rounded-full flex-1 bg-gradient-to-r from-violet-500 to-pink-500 hover:opacity-90 text-white shadow-lg shadow-fuchsia-200/40"
                    data-testid="ai-confirm-create"
                  >
                    {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />}
                    Confirmar y crear
                  </Button>
                </div>
              ) : (
                <>
                  <textarea
                    ref={textareaRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); sendMessage(); }
                    }}
                    placeholder={messages.length === 0 ? `Ej: ${meta.examples[0]}` : 'Tu respuesta…'}
                    rows={messages.length === 0 ? 3 : 2}
                    disabled={busy}
                    className="w-full resize-none rounded-2xl border border-stone-200 bg-stone-50 focus:bg-white focus:border-violet-300 focus:ring-2 focus:ring-violet-100 focus:outline-none p-3 text-sm transition-all"
                    data-testid="ai-create-input"
                  />
                  <div className="flex items-center gap-2 flex-wrap">
                    {!recording ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={startRecording}
                        disabled={busy}
                        className="rounded-full border-violet-200 text-violet-700 hover:bg-violet-50"
                        data-testid="ai-record-start"
                      >
                        <Mic className="w-4 h-4 mr-1.5" /> Grabar audio
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        size="sm"
                        onClick={stopRecording}
                        className="rounded-full bg-red-500 hover:bg-red-600 text-white animate-pulse"
                        data-testid="ai-record-stop"
                      >
                        <Square className="w-4 h-4 mr-1.5" /> Parar
                      </Button>
                    )}
                    <Button
                      type="button"
                      onClick={() => sendMessage()}
                      disabled={busy || !input.trim()}
                      className="rounded-full ml-auto bg-gradient-to-r from-violet-500 to-pink-500 hover:opacity-90 text-white"
                      data-testid="ai-send"
                    >
                      {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Send className="w-4 h-4 mr-1.5" />}
                      Enviar
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AiCreateButton;
